/* oxlint-disable no-inner-declarations */
import { ok } from '@oazapfts/runtime';
import * as rq from '@tanstack/react-query';

import type { TaskPreview } from '@/task-manager/entities/task-preview';
import { Optimistic } from '@/utils/react-query/optimistic';

import { type BodyOf, tasksV1CardsDeleteDestroy } from '../fetch-client';
import { GetTask } from '../queries/get-task';
import { ListTasks } from '../queries/list-tasks';
import { useFetchOpts } from '../use-fetch-opts';

export namespace DeleteTask {
  export type Input = Pick<TaskPreview, 'id'>;
  export type Output = void;

  function bodyFromInput(
    input: Input,
  ): BodyOf<typeof tasksV1CardsDeleteDestroy> {
    return { uuid: input.id };
  }

  export function useMutation() {
    const getOpts = useFetchOpts();
    const queryClient = rq.useQueryClient();
    const [listTasksFilters] = ListTasks.useGetFiltersFromQueryParams();

    return rq.useMutation<Output, unknown, Input>({
      async mutationFn(input) {
        const opts = await getOpts();
        const body = bodyFromInput(input);
        await ok(tasksV1CardsDeleteDestroy(body, opts));
        return;
      },
      onMutate(input) {
        queryClient.cancelQueries(GetTask.key({ id: input.id }));
        queryClient.cancelQueries(ListTasks.key({ filters: listTasksFilters }));
        queryClient.setQueryData(
          ListTasks.key({ filters: listTasksFilters }),
          ListTasks.optimistic((previous) => ({
            ...previous,
            tasks: Optimistic.deleteItem(
              previous.tasks,
              ({ id }) => id === input.id,
            ),
          })),
        );
      },
      async onSettled(_, __, { id }) {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: GetTask.key({ id }),
          }),
          queryClient.invalidateQueries({
            queryKey: ListTasks.key({ filters: listTasksFilters }),
          }),
          queryClient.invalidateQueries({
            queryKey: ['project-management'],
          }),
        ]);
      },
    });
  }
}
